import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { addCustomHostname, getCustomHostnameDns, removeCustomHostname } from '../api/subscriptions';

/**
 * Live CNAME check for each custom domain on a UOS service. Keyed on the domain list so
 * adding or removing a domain re-checks. DNS changes propagate slowly, so cache 5 minutes.
 */
export function useCustomHostnameDns(subscriptionId: string, hostnames: string[], enabled: boolean) {
  return useQuery({
    queryKey: ['custom-hostname-dns', subscriptionId, hostnames.join(',')],
    queryFn: () => getCustomHostnameDns(subscriptionId),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useAddCustomHostname() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      subscriptionId,
      hostname,
      confirmations,
    }: {
      subscriptionId: string;
      hostname: string;
      confirmations?: { ip_warning?: boolean; wildcard_warning?: boolean };
    }) => addCustomHostname(subscriptionId, hostname, confirmations),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
    },
  });
}

export function useRemoveCustomHostname() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ subscriptionId, hostname }: { subscriptionId: string; hostname: string }) =>
      removeCustomHostname(subscriptionId, hostname),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
    },
  });
}
